import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'use-intl'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchJournalEntryWithItems } from '../../clients/journalEntryApiClient'
import type { JournalEntryWithSource } from '../../clients/journalEntryApiClient'
import { InvoiceApiClient } from '@/clients/invoiceApiClient'
import { FileText } from 'lucide-react'
import ClosingEntryFxSummary from '@/components/shared/ClosingEntryFxSummary'
import { useJournalSourceLabel } from './useJournalLabels'

type Props = {
  entry: JournalEntryWithSource
  onClose: () => void
}

export function JournalEntryDrawer({ entry, onClose }: Props) {
  const t = useTranslations('consultantBooks.journal.drawer')
  const sourceLabel = useJournalSourceLabel()
  const { data, isLoading } = useQuery({
    queryKey: ['journal-entry-items', entry.entry.id],
    queryFn: () => fetchJournalEntryWithItems(entry.entry.id),
  })

  const { data: documentUrl } = useQuery({
    queryKey: ['journal-entry-doc-url', data?.documentFilePath],
    queryFn: () => InvoiceApiClient.getInvoiceFileUrl(data!.documentFilePath!),
    enabled: !!data?.documentFilePath,
  })

  const verLabel = `#${entry.entry.journal_number}`
  const importedAs = entry.originalSeries && entry.originalNumber != null
    ? `${entry.originalSeries}${entry.originalNumber}`
    : null

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {verLabel} — {entry.entry.title}
          </DialogTitle>
          {importedAs && (
            <p className="text-xs text-muted-foreground">{t('importedAs', { value: importedAs })}</p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">{t('transactionDate')}</span>
              <p className="font-medium mt-0.5">{entry.entry.date}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('bookingDate')}</span>
              <p className="font-medium mt-0.5">{entry.regDate ?? entry.entry.created_at?.slice(0, 10) ?? entry.entry.date}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('verification')}</span>
              <p className="font-medium mt-0.5">{verLabel}</p>
            </div>
            <div>
              <span className="text-muted-foreground">{t('source')}</span>
              <p className="font-medium mt-0.5">{sourceLabel(entry.sourceType)}</p>
            </div>
          </div>

          {data?.closingFx && (
            <ClosingEntryFxSummary closingFx={data.closingFx} items={data.items} />
          )}

          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <FileText className="h-4 w-4 shrink-0" />
              {data?.documentFileName ?? t('viewDocument')}
            </a>
          )}

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : (
            <div className="rounded border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-inset border-b border-border">
                  <tr className="text-left text-muted-foreground text-xs">
                    <th className="px-3 py-2 font-medium">{t('account')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('debit')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('credit')}</th>
                    <th className="px-3 py-2 font-medium hidden sm:table-cell">{t('description')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items ?? []).map((item, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 font-mono text-xs font-medium">{item.account_number}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.debit > 0 ? item.debit.toFixed(2) : ''}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.credit > 0 ? item.credit.toFixed(2) : ''}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs hidden sm:table-cell truncate max-w-[140px]">
                        {item.description ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-border bg-surface-inset">
                  <tr className="text-xs font-medium">
                    <td className="px-3 py-2 text-muted-foreground">{t('total')}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(data?.items ?? []).reduce((s, i) => s + (i.debit ?? 0), 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(data?.items ?? []).reduce((s, i) => s + (i.credit ?? 0), 0).toFixed(2)}
                    </td>
                    <td className="hidden sm:table-cell" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
